import cn from 'classnames';
import { Children, cloneElement, forwardRef, isValidElement } from 'react';

import type { ForwardedRef } from 'react';

import type { ButtonGroupProps } from './button-group.types';

import './button-group.scss';

function getGroupClasses(type: ButtonGroupProps['type']): string {
  if (type === 'primary') {
    return 'rbui-button-group--primary';
  }

  if (type === 'tertiary') {
    return 'rbui-button-group--tertiary';
  }

  return 'rbui-button-group--secondary';
}

function getGroupButtonPositionClass(first: boolean, last: boolean): string {
  if (first && last) {
    return '';
  }

  if (first) {
    return 'rbui-button-group__button--first';
  }

  if (last) {
    return 'rbui-button-group__button--last';
  }

  return 'rbui-button-group__button--middle';
}

/**
 * The button group component can be used to group related buttons.
 *
 * ## Usage
 * ```jsx
 * import { Button, ButtonGroup } from '@bloomreach/react-banana-ui';
 *
 * function MyButtonGroup() {
 *   return (
 *     <ButtonGroup>
 *       <Button>One</Button>
 *       <Button>Two</Button>
 *     </ButtonGroup>
 *   );
 * }
 * ```
 */
const ButtonGroup = forwardRef((props: ButtonGroupProps, forwardedRef: ForwardedRef<HTMLDivElement>): JSX.Element => {
  const { children, className, disabled, type = 'secondary', ...rest } = props;

  const classes = cn(className, 'rbui-button-group', getGroupClasses(type), {
    'rbui-button-group--disabled': disabled,
  });
  const childrenCount = Children.count(children);

  return (
    <div aria-disabled={disabled} className={classes} ref={forwardedRef} role="group" {...rest}>
      {Children.map(children, (button, index) => {
        if (!isValidElement(button)) {
          return;
        }

        const first = index === 0;
        const last = index === childrenCount - 1;
        const buttonDisabled = button.props.disabled ?? disabled;
        const buttonClasses = cn(
          'rbui-button-group__button',
          { 'rbui-button-group__button--disabled': buttonDisabled },
          getGroupButtonPositionClass(first, last),
          button.props.className,
        );

        return cloneElement(button as React.ReactElement, {
          className: buttonClasses,
          disabled: buttonDisabled,
          key: button.key ?? index,
          type: button.props.type ?? type,
        });
      })?.filter(Boolean)}
    </div>
  );
});

ButtonGroup.displayName = 'ButtonGroup';

export default ButtonGroup;
